import type { DailyChallenge } from '../shared/gameplay-fun.js';
import {
  readLocalCareerSection,
  updateLocalCareerSection,
} from './local-career-storage.js';

export interface DailyChallengeRecord {
  id: string;
  attempts: number;
  bestLapMs: number | null;
  completedAt: number | null;
}

export interface DailyChallengeState {
  version: 1;
  awardedRaceIds: string[];
  records: Record<string, DailyChallengeRecord>;
}

const STORAGE_KEY = 'nightshift:daily-challenge:v1';
const MAX_RACE_IDS = 96;

export function createDailyChallengeState(): DailyChallengeState {
  return { version: 1, awardedRaceIds: [], records: {} };
}

export function parseDailyChallengeState(raw: string | null): DailyChallengeState {
  if (!raw) return createDailyChallengeState();
  try {
    const candidate = JSON.parse(raw) as { awardedRaceIds?: unknown; records?: unknown };
    if (!candidate.records || typeof candidate.records !== 'object') return createDailyChallengeState();
    const records: Record<string, DailyChallengeRecord> = {};
    for (const [id, value] of Object.entries(candidate.records)) {
      if (!validUtcDateId(id) || !value || typeof value !== 'object') continue;
      const record = value as Partial<DailyChallengeRecord>;
      records[id] = {
        id,
        attempts: integer(record.attempts),
        bestLapMs: positive(record.bestLapMs),
        completedAt: positive(record.completedAt),
      };
    }
    const recent = Object.keys(records).sort().slice(-32);
    return {
      version: 1,
      awardedRaceIds: Array.isArray(candidate.awardedRaceIds)
        ? [...new Set(candidate.awardedRaceIds.filter(validRaceId))].slice(-MAX_RACE_IDS)
        : [],
      records: Object.fromEntries(recent.map((id) => [id, records[id]!])),
    };
  } catch {
    return createDailyChallengeState();
  }
}

export function loadDailyChallengeState(): DailyChallengeState {
  try {
    const career = readLocalCareerSection('dailyChallenges');
    if (career !== undefined) return parseDailyChallengeState(JSON.stringify(career));
    return parseDailyChallengeState(localStorage.getItem(STORAGE_KEY));
  } catch {
    return createDailyChallengeState();
  }
}

export function dailyChallengeRecord(
  state: DailyChallengeState,
  challenge: Pick<DailyChallenge, 'id'>,
): DailyChallengeRecord {
  return state.records[challenge.id] ?? {
    id: challenge.id,
    attempts: 0,
    bestLapMs: null,
    completedAt: null,
  };
}

export function recordDailyChallengeResult(
  state: DailyChallengeState,
  challengeId: string,
  lapMs: number | null,
  now = Date.now(),
  raceId = '',
  persist = true,
): DailyChallengeState {
  if (!validUtcDateId(challengeId) || (validRaceId(raceId) && state.awardedRaceIds.includes(raceId))) {
    return state;
  }
  const before = state.records[challengeId] ?? {
    id: challengeId,
    attempts: 0,
    bestLapMs: null,
    completedAt: null,
  };
  const validLap = positive(lapMs);
  const next: DailyChallengeState = {
    version: 1,
    awardedRaceIds: validRaceId(raceId)
      ? [...state.awardedRaceIds, raceId].slice(-MAX_RACE_IDS)
      : state.awardedRaceIds,
    records: {
      ...state.records,
      [challengeId]: {
        id: challengeId,
        attempts: before.attempts + 1,
        bestLapMs: validLap === null
          ? before.bestLapMs
          : before.bestLapMs === null ? validLap : Math.min(before.bestLapMs, validLap),
        completedAt: validLap === null ? before.completedAt : now,
      },
    },
  };
  if (persist) {
    const envelopeUpdated = updateLocalCareerSection('dailyChallenges', next);
    if (envelopeUpdated === null) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Daily history is a local convenience and must never block the event.
      }
    }
  }
  return next;
}

function validUtcDateId(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function validRaceId(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9:_-]{1,160}$/.test(value);
}

function integer(value: unknown): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(Number(value))) : 0;
}

function positive(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}
